import { hideMobileAdWhenFooterVisible } from '../modules/ads.js';

/* global googletag */

/**
 * Debounce function.
 *
 * @param {Function} func The function to run.
 * @param {number}   wait The debounce time.
 *
 * @return {Function} The debounced function.
 */
const debounce = (func, wait) => {
  let timeout;
  return function (...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  };
};

/**
 * Utility to prevent default triggering on an event.
 *
 * @param {Event} e The triggered event.
 *
 * @return {void}
 */
const preventSummaryClick = (e) => {
  e.preventDefault();
};

/**
 * Disable default <details> functionalty for larger screens.
 *
 * @param {NodeList} footerDetails The NodeList of footer <details> elements.
 *
 * @return {void}
 */
const disableFooterDetailsForDesktop = (footerDetails) => {
  footerDetails.forEach((detail) => {
    const summary = detail.querySelector('summary');

    if (!summary) return;

    detail.setAttribute('open', '');
    summary.setAttribute('aria-disabled', 'true');
    summary.setAttribute('tabindex', '-1');

    summary.addEventListener('click', preventSummaryClick);
  });
};

/**
 * Resumes default <details> functionalty for smaller screens.
 *
 * @param {NodeList} footerDetails The NodeList of footer <details> elements.
 *
 * @return {void}
 */
const enableFooterDetailsForMobile = (footerDetails) => {
  footerDetails.forEach((detail) => {
    const summary = detail.querySelector('summary');

    if (!summary) return;

    detail.removeAttribute('open');
    summary.removeAttribute('aria-disabled');
    summary.removeAttribute('tabindex');
    summary.removeEventListener('click', preventSummaryClick);
  });
};

/**
 * Determine the required state for <detail> elements in the footer.
 *
 * @return {void}
 */
const determineFooterDetailsState = () => {
  const windowWidth = window.innerWidth;
  const footerDetails = document.querySelectorAll('footer .footer-nav details');

  if (!footerDetails.length) return;

  if (windowWidth > 781) {
    disableFooterDetailsForDesktop(footerDetails);
  } else {
    enableFooterDetailsForMobile(footerDetails);
  }
};

/**
 * Remove the interstitial ad slot and container.
 *
 * @param {HTMLElement} interstitialElement The ad slot container.
 * @param {number}      interval            The remaining timer of the interstitial.
 *
 * @return {void}
 */
const removeInterstitialAdSlot = (interstitialElement, interval) => {
  if (!interstitialElement) return;

  interstitialElement.remove();
  if (interval) clearInterval(interval);
};

/**
 * Activate and begin timer for interstitial ad slot.
 *
 * @param {string} slotId The ad slot ID to use for the interstitial.
 *
 * @return {void}
 */
const activateInterstitialAdSlot = (slotId) => {
  if (!slotId) return;

  const interstitialSlot = document.getElementById(slotId);
  if (!interstitialSlot) return;

  const interstitialContainer = interstitialSlot.closest('.interstitial-container');
  if (!interstitialContainer) return;

  const interstitialHeader = interstitialContainer.querySelector('.interstitial-header');
  if (!interstitialHeader) return;

  const interstitialCloseButton = interstitialHeader.querySelector(
    '.wp-block-buttons > .wp-block-button > button',
  );
  if (!interstitialCloseButton) return;

  let sec = window?.MediaPressAdsOptions?.mediapress_ads_interstitial_interval ?? 5;
  const REMOVE_AFTER_SECONDS = -10;

  const intervalMessage = document.createElement('p');
  intervalMessage.classList.add('interstitial-interval');
  intervalMessage.setAttribute('aria-live', 'polite');
  intervalMessage.innerHTML = `You can skip this ad in ${sec}`;

  interstitialHeader.appendChild(intervalMessage);

  interstitialContainer.classList.add('interval', 'active');

  const interval = setInterval(() => {
    sec -= 1;
    if (sec > 0) {
      intervalMessage.innerHTML = `You can skip this ad in ${sec}`;
    } else if (sec === 0) {
      intervalMessage.remove();
      interstitialContainer.classList.remove('interval');
      interstitialCloseButton.focus();
      interstitialCloseButton.addEventListener(
        'click',
        () => {
          removeInterstitialAdSlot(interstitialContainer, interval);
        },
        { once: true },
      );
    } else if (sec === REMOVE_AFTER_SECONDS) {
      removeInterstitialAdSlot(interstitialContainer, interval);
    }
  }, 1000);
};

/**
 * Ensure googletag is defined before triggering interstitial
 */
window.googletag = window.googletag || {};
window.googletag.cmd = window.googletag.cmd || [];
window.googletag.cmd.push(() => {
  googletag.pubads().addEventListener('slotRenderEnded', (event) => {
    const slotId = event.slot.getSlotElementId();
    if (!event.isEmpty && slotId === 'interstitial') {
      activateInterstitialAdSlot(slotId);
    }
  });
});

const pulldownLeaderboard = document.querySelector('.leaderboard-pulldown');
const mpaSlots = document.querySelectorAll(
  'div:not(.leaderboard-pulldown, .interstitial-container):has(> .wp-block-mpa-slot) .wp-block-mpa-slot',
);
let shouldDetectPulldownPosition = true;

/**
 * Removes the sticky leaderboard pulldown classes in sequence,
 *
 * @param {HTMLElement} leaderboard The pulldown leaderboard element.
 *
 * @return {void}
 */
const removePulldownSticky = (leaderboard) => {
  leaderboard.classList.add('hide-pulldown');
  setTimeout(() => {
    leaderboard.classList.remove('leaderboard-pulldown');
    shouldDetectPulldownPosition = false;
  }, 250);
};

/**
 * Checks if the leaderboard pulldown ad slot has scrolled past any other ad slot.
 * If so, removes the pulldown class from the leaderboard element.
 *
 * @param {HTMLElement} leaderboard The pulldown leaderboard element.
 * @param {NodeList}    adSlots     The list of ad slot elements.
 *
 * @return {void}
 */
const detectPulldownAdSlotOverlap = (leaderboard, adSlots) => {
  if (!leaderboard || !adSlots.length || !shouldDetectPulldownPosition) return;

  const pulldownRect = leaderboard.getBoundingClientRect();

  adSlots.forEach((adSlot) => {
    const adRect = adSlot.getBoundingClientRect();
    const isScrolledPast = pulldownRect.bottom > adRect.top;

    if (isScrolledPast) {
      removePulldownSticky(leaderboard);
    }
  });
};

/**
 * Detects the user page scroll depth.
 *
 * @param {HTMLElement} leaderboard The pulldown leaderboard element.
 * @param {number}      scrollDepth The depth in which to remove the sticky pulldown.
 *
 * @return {void}
 */
function detectPageScrollDepth(leaderboard, scrollDepth) {
  const scrollTop = window.scrollY;
  const viewportHeight = window.innerHeight;
  const totalHeight = document.documentElement.scrollHeight;
  const scrollPercent = ((scrollTop + viewportHeight) / totalHeight) * 100;

  if (scrollPercent >= scrollDepth) {
    removePulldownSticky(leaderboard);
  }
}

/**
 * Handle the Pulldown ad slot display based on user scroll.
 *
 * @param {HTMLElement} leaderboard The pulldown leaderboard element.
 * @param {NodeList}    adSlots     The list of ad slot elements.
 *
 * @return {void}
 */
const handlePulldownDisplay = (leaderboard, adSlots) => {
  if (!leaderboard || !shouldDetectPulldownPosition) return;

  if (!adSlots.length) {
    detectPageScrollDepth(leaderboard, 50);
  } else {
    detectPulldownAdSlotOverlap(leaderboard, adSlots);
  }
};

window.addEventListener('DOMContentLoaded', () => {
  determineFooterDetailsState();
  handlePulldownDisplay(pulldownLeaderboard, mpaSlots);
  hideMobileAdWhenFooterVisible();
});

window.addEventListener(
  'resize',
  debounce(() => {
    determineFooterDetailsState();
    handlePulldownDisplay(pulldownLeaderboard, mpaSlots);
  }, 100),
);

window.addEventListener('scroll', () => {
  handlePulldownDisplay(pulldownLeaderboard, mpaSlots);
});
